import { useMemo } from 'react';
import { useSchedulingContext } from '../../pages/schedules/SchedulingCanvas';
import { useCoverageAnalytics } from '../../hooks/useScheduleIntervals';

export default function MetricsRow() {
  const { cursorPosition, setFilterCriteria, channelFilter, scheduleId, selectedDate } = useSchedulingContext();

  // Fetch real coverage data from API (same data as chart)
  const { data: analyticsData, isLoading } = useCoverageAnalytics({
    scheduleId: scheduleId || undefined,
    date: selectedDate,
    channel: channelFilter === 'TOTAL' ? undefined : channelFilter,
  });

  // Transform data for metrics row
  const metricsData = useMemo(() => {
    if (!analyticsData?.hourly) return [];

    return analyticsData.hourly.map((item, index) => ({
      hour: index,
      netStaffing: item.actual - item.required,
      required: item.required,
      actual: item.actual,
    }));
  }, [analyticsData]);

  const handleDeficitClick = (hour: number, netStaffing: number) => {
    if (netStaffing < 0) {
      // Filter agents who are eligible for this channel but on break/unscheduled
      setFilterCriteria({
        search: '',
        teams: [],
        skills: [],
        channels: [],
        statuses: [],
        availability: {
          hour,
          channel: channelFilter === 'TOTAL' ? 'PHONE' : channelFilter,
        },
      });
    }
  };

  // Loading state
  if (isLoading || metricsData.length === 0) {
    return (
      <div className="px-6 py-3">
        <div className="flex items-center space-x-1">
          <div className="w-32 flex-shrink-0">
            <span className="text-sm font-medium text-gray-700">Net Staffing</span>
          </div>
          <div className="flex-1 text-center text-sm text-gray-400">
            {isLoading ? 'Loading...' : 'No data'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-3 relative">
      <div className="flex items-center space-x-1">
        {/* Label */}
        <div className="w-32 flex-shrink-0">
          <span className="text-sm font-medium text-gray-700">Net Staffing</span>
        </div>

        {/* Metric cells aligned to chart intervals */}
        <div className="flex-1 flex items-center relative">
          {/* Vertical cursor sync line */}
          {cursorPosition !== null && (
            <div
              className="absolute top-0 bottom-0 w-px bg-blue-400 z-10 pointer-events-none"
              style={{
                left: `${(cursorPosition / 24) * 100}%`,
              }}
            />
          )}

          {metricsData.map((metric) => {
            const isDeficit = metric.netStaffing < 0;
            const isSurplus = metric.netStaffing > 0;
            const isHighlighted = cursorPosition === metric.hour;

            return (
              <div
                key={metric.hour}
                className={`flex-1 text-center py-2 cursor-pointer transition-all ${
                  isHighlighted ? 'bg-gray-100' : ''
                } ${isDeficit ? 'hover:bg-red-50' : ''}`}
                onClick={() => handleDeficitClick(metric.hour, metric.netStaffing)}
                title={
                  isDeficit
                    ? `Click to show available agents for ${metric.hour}:00 (${channelFilter})`
                    : `${metric.hour}:00 - Net: ${metric.netStaffing > 0 ? '+' : ''}${metric.netStaffing}`
                }
              >
                <span
                  className={`text-xs font-semibold ${
                    isDeficit
                      ? 'text-red-700 bg-red-100 px-2 py-1 rounded'
                      : isSurplus
                      ? 'text-green-700'
                      : 'text-gray-600'
                  }`}
                >
                  {metric.netStaffing > 0 ? '+' : ''}
                  {metric.netStaffing.toFixed(1)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Helper text */}
      <div className="mt-2 text-xs text-gray-500">
        <span className="text-red-600 font-medium">Red</span> indicates understaffing. Click to filter agents available to fill gaps.
      </div>
    </div>
  );
}
